import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MdOutlineDashboard,
  MdAttachMoney,
  MdAssignment,
  MdSupportAgent,
  MdSettings,
  MdLogout,
  MdPayment,
  MdReceipt,
  MdPersonAddAlt,
  MdEvent,
  MdMenu,
  MdClose,
  MdWarning,
} from "react-icons/md";
import { LuTableProperties } from "react-icons/lu";
import { Link, useLocation, useParams } from "react-router-dom";
import { removeAccessToken } from "../utils/api/authToken";
import { IoMdNotificationsOutline } from "react-icons/io";
import { FaChevronDown } from "react-icons/fa6";
import logo from "../assets/miles_logo.svg";
import { AuthContext } from "../utils/context/AuthContext";
import { purgeAccount } from "../utils/api/api";
import toast from "react-hot-toast";

const TopMenu = ({ header }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const location = useLocation();
  const { id } = useParams();
  const { user } = useContext(AuthContext) || { user: null };

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileOpen && !event.target.closest(".profile-dropdown")) {
        setIsProfileOpen(false);
      }
      if (
        isNotificationOpen &&
        !event.target.closest(".notification-dropdown")
      ) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileOpen, isNotificationOpen]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleProfile = () => {
    setIsProfileOpen(!isProfileOpen);
    if (isNotificationOpen) setIsNotificationOpen(false);
  };

  const toggleNotification = () => {
    setIsNotificationOpen(!isNotificationOpen);
    if (isProfileOpen) setIsProfileOpen(false);
  };

  const openDeleteModal = () => {
    setIsDeleteModalOpen(true);
    setIsProfileOpen(false);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteConfirmation("");
    setIsDeleting(false);
  };

  const deleteAccount = async () => {
    if (deleteConfirmation.toLowerCase() !== "delete") {
      return;
    }

    if (!user?.id) {
      alert("User ID not found. Please try logging in again.");
      return;
    }

    setIsDeleting(true);

    try {
      const response = await purgeAccount(user.id);
      removeAccessToken();
      window.location.href = "/";
      toast.success("Account deleted successfully.");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Error deleting account. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const isActive = (paths) => {
    const active = paths.includes(location.pathname);
    return active;
  };

  const notifications = [
    { id: 1, message: "Payment received", time: "2 hours ago", isRead: false },
    {
      id: 2,
      message: "New contract available",
      time: "Yesterday",
      isRead: false,
    },
    {
      id: 3,
      message: "Property viewing scheduled",
      time: "2 days ago",
      isRead: true,
    },
  ];

  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.05,
        delayChildren: 0.1,
      },
    },
    closed: {
      x: "-100%",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
  };

  const menuItemVariants = {
    open: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
    closed: {
      y: 20,
      opacity: 0,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, height: 0 },
    visible: {
      opacity: 1,
      y: 0,
      height: "auto",
      transition: { duration: 0.2 },
    },
    exit: {
      opacity: 0,
      y: -10,
      height: 0,
      transition: { duration: 0.2 },
    },
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.2 },
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.2 },
    },
  };

  const menuItems = [
    {
      path: "/dashboard",
      icon: <MdOutlineDashboard />,
      label: "Dashboard",
      activeRoutes: ["/dashboard"],
    },
    {
      path: "/dashboard/orders",
      icon: <MdAttachMoney />,
      label: "Pay Now",
      activeRoutes: ["/dashboard/orders", `/dashboard/orders/${id}/view`],
    },
    {
      path: "/dashboard/my-properties",
      icon: <LuTableProperties />,
      label: "My Properties",
      activeRoutes: ["/dashboard/my-properties"],
    },
    {
      path: "/dashboard/contracts",
      icon: <MdAssignment />,
      label: "Contracts",
      activeRoutes: [
        "/dashboard/contracts",
        `/dashboard/contracts/${id}/view-details`,
      ],
    },
    {
      path: "/dashboard/invoices",
      icon: <MdReceipt />,
      label: "Invoices",
      activeRoutes: ["/dashboard/invoices", `/dashboard/invoices/${id}/view`],
    },
    {
      path: "/dashboard/payments",
      icon: <MdPayment />,
      label: "My Statement",
      activeRoutes: ["/dashboard/payments"],
    },
    {
      path: "/dashboard/bookings",
      icon: <MdEvent />,
      label: "Bookings",
      activeRoutes: ["/dashboard/bookings"],
    },
    {
      path: "/dashboard/support",
      icon: <MdSupportAgent />,
      label: "Support",
      activeRoutes: ["/dashboard/support"],
    },
    {
      path: "/dashboard/referral",
      icon: <MdPersonAddAlt />,
      label: "Referral",
      activeRoutes: ["/dashboard/referral"],
    },
    {
      path: "/dashboard/settings",
      icon: <MdSettings />,
      label: "Settings",
      activeRoutes: ["/dashboard/settings"],
    },
  ];

  return (
    <div>
      <header className="h-16 flex items-center px-4 md:px-6 bg-white/90 backdrop-blur-md border-b border-gray-100 sticky top-0 z-30 shadow-sm">
        <div className="flex items-center gap-4 w-full">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img src={logo} className="h-10 w-auto" alt="Logo" />
            </Link>
          </div>

          <button
            onClick={toggleSidebar}
            className="md:hidden p-2 rounded-full hover:bg-gray-100"
            aria-label="Toggle menu"
          >
            {isSidebarOpen ? (
              <MdClose className="text-gray-700" size={24} />
            ) : (
              <MdMenu className="text-gray-700" size={24} />
            )}
          </button>

          <h1 className="text-gray-800 font-medium text-lg hidden sm:block">
            {header}
          </h1>

          <div className="flex-grow"></div>

          <div className="flex items-center gap-1 md:gap-3">
            <div className="relative notification-dropdown">
              <button
                onClick={toggleNotification}
                className="p-2 rounded-full hover:bg-gray-100 relative"
                aria-label="Notifications"
              >
                <IoMdNotificationsOutline className="text-gray-700" size={22} />
                {notifications.filter((n) => !n.isRead).length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>

              <AnimatePresence>
                {isNotificationOpen && (
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={dropdownVariants}
                    className="absolute right-0 mt-2 w-72 bg-white/95 backdrop-blur-lg rounded-xl border border-gray-100 shadow-lg z-40 overflow-hidden"
                  >
                    <div className="p-3 border-b border-gray-100">
                      <div className="flex justify-between items-center">
                        <h3 className="font-medium text-gray-800">
                          Notifications
                        </h3>
                        <span className="text-xs text-primary">
                          Mark all as read
                        </span>
                      </div>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length > 0 ? (
                        notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`p-3 border-b border-gray-50 hover:bg-gray-50 ${
                              !notification.isRead ? "bg-blue-50/30" : ""
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={`w-2 h-2 rounded-full mt-2 ${
                                  !notification.isRead
                                    ? "bg-primary"
                                    : "bg-gray-300"
                                }`}
                              ></div>
                              <div>
                                <p className="text-sm text-gray-800">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {notification.time}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          No notifications
                        </div>
                      )}
                    </div>
                    <div className="p-2 border-t border-gray-100 bg-gray-50/50">
                      <button className="w-full py-2 text-primary text-sm font-medium text-center">
                        View all notifications
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="relative profile-dropdown">
              <button
                onClick={toggleProfile}
                className="flex items-center gap-2 py-1 px-2 rounded-full hover:bg-gray-100"
                aria-label="User profile"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-primary/80 to-secondary/80 flex items-center justify-center text-white text-sm font-medium">
                  {user?.first_name?.charAt(0) || "U"}
                </div>
                <span className="hidden md:block text-sm font-medium text-gray-700 max-w-[120px] truncate">
                  {user?.first_name || "User"}
                </span>
                <FaChevronDown
                  className={`text-gray-400 text-xs transition-transform ${
                    isProfileOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              <AnimatePresence>
                {isProfileOpen && (
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={dropdownVariants}
                    className="absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-lg rounded-xl border border-gray-100 shadow-lg z-40 overflow-hidden"
                  >
                    <div className="p-4 border-b border-gray-100">
                      <p className="font-medium text-gray-800">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {user?.email}
                      </p>
                    </div>
                    <div className="p-2">
                      <Link
                        to="/dashboard/settings"
                        className="block py-2 px-3 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
                      >
                        Account Settings
                      </Link>
                      <Link
                        to="/dashboard/support"
                        className="block py-2 px-3 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
                      >
                        Help & Support
                      </Link>
                      <button
                        onClick={openDeleteModal}
                        className="block py-2 px-3 rounded-lg text-gray-700 hover:bg-gray-50 text-sm w-full text-left"
                      >
                        Delete Account
                      </button>
                    </div>
                    <div className="p-2 border-t border-gray-100">
                      <button
                        onClick={removeAccessToken}
                        className="w-full py-2 px-3 text-left rounded-lg text-red-600 hover:bg-red-50 text-sm flex items-center gap-2"
                      >
                        <MdLogout size={16} />
                        <span>Log Out</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      {/* Delete Account Confirmation Modal */}
      <AnimatePresence>
        {isDeleteModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={closeDeleteModal}
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-white rounded-2xl max-w-md w-full mx-4 overflow-hidden shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <MdWarning className="text-red-600" size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Delete Account
                    </h3>
                    <p className="text-sm text-gray-500">
                      This action cannot be undone
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-medium text-red-900 mb-2">
                      ⚠️ Warning: This is irreversible!
                    </h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>
                        • All your account data will be permanently deleted
                      </li>
                      <li>
                        • Your properties, contracts, and payment history will
                        be lost
                      </li>
                      <li>
                        • Any active bookings or pending transactions will be
                        cancelled
                      </li>
                      <li>
                        • You will lose access to all services immediately
                      </li>
                    </ul>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      To confirm deletion, type{" "}
                      <span className="font-bold text-red-600">DELETE</span>{" "}
                      below:
                    </label>
                    <input
                      type="text"
                      value={deleteConfirmation}
                      onChange={(e) => setDeleteConfirmation(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                      placeholder="Type DELETE to confirm"
                      disabled={isDeleting}
                    />
                  </div>

                  <div className="text-xs text-gray-500">
                    <p>
                      If you're having issues with your account, please{" "}
                      <Link
                        to="/dashboard/support"
                        className="text-primary hover:underline"
                      >
                        contact our support team
                      </Link>{" "}
                      instead of deleting your account.
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-gray-100 bg-gray-50">
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={closeDeleteModal}
                    disabled={isDeleting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={deleteAccount}
                    disabled={
                      deleteConfirmation.toLowerCase() !== "delete" ||
                      isDeleting
                    }
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isDeleting && (
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    )}
                    {isDeleting ? "Deleting..." : "Delete Account"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={toggleSidebar}
          ></motion.div>
        )}
      </AnimatePresence>

      <motion.aside
        variants={sidebarVariants}
        initial="closed"
        animate={isSidebarOpen ? "open" : "closed"}
        className="fixed top-0 left-0 h-full w-[280px] bg-white/95 backdrop-blur-lg border-r border-gray-100 z-50 md:translate-x-0 md:z-20 md:top-16 md:h-[calc(100vh-4rem)] shadow-lg md:shadow-none overflow-y-auto"
      >
        <div className="p-4 md:hidden flex items-center justify-between border-b border-gray-100">
          <Link
            to="/"
            className="flex items-center"
            onClick={() => setIsSidebarOpen(false)}
          >
            <img src={logo} className="h-10 w-auto" alt="Logo" />
          </Link>
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <MdClose className="text-gray-700" size={24} />
          </button>
        </div>

        <div className="p-4">
          <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
            General
          </h3>
          <nav className="space-y-1">
            {menuItems.map((item, index) => (
              <motion.div key={index} variants={menuItemVariants}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive(item.activeRoutes)
                      ? "bg-primary/10 text-primary"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <span
                    className={`
                                        w-8 h-8 flex items-center justify-center rounded-full
                                        ${
                                          isActive(item.activeRoutes)
                                            ? "bg-primary/10"
                                            : "bg-gray-100"
                                        }
                                    `}
                  >
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                  {isActive(item.activeRoutes) && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="ml-auto w-1.5 h-1.5 rounded-full bg-primary"
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                    />
                  )}
                </Link>
              </motion.div>
            ))}

            <motion.div variants={menuItemVariants}>
              <button
                onClick={removeAccessToken}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all w-full text-left text-gray-700 hover:bg-red-50 hover:text-red-600"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100">
                  <MdLogout />
                </span>
                <span>Log Out</span>
              </button>
            </motion.div>
          </nav>
        </div>

        <div className="mt-6 mx-4 p-4 rounded-xl bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/10">
          <h4 className="text-sm font-medium text-gray-800">Need Help?</h4>
          <p className="text-xs text-gray-600 mt-1">
            Our support team is available 24/7
          </p>
          <Link
            to="/dashboard/support"
            className="mt-3 text-xs font-medium text-primary flex items-center gap-1"
          >
            Contact Support
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </Link>
        </div>
      </motion.aside>
    </div>
  );
};

export default TopMenu;
